<template>
  <Skeleton v-if="!group"></Skeleton>

  <div v-else class="border rounded-lg pa-4">
    <div
      v-if="parallelGroupsCount > 1"
      class="route-parallel-info d-flex align-center flex-wrap mb-4">
      <v-icon icon="mdi-drag-horizontal" size="small"></v-icon>

      <div class="flex-grow-1">
        <div class="route-parallel-info__title">
          Паралельний етап · {{ parallelGroupsCount }} групи
        </div>
        <div class="route-parallel-info__subtitle">
          Усі групи цього етапу погоджують одночасно
        </div>
      </div>

      <v-btn
        border
        color="deep-purple-accent-3"
        variant="text"
        @click="emit('add-to-stage')">
        <v-icon class="mr-1" icon="mdi-plus"></v-icon>
        Додати в етап
      </v-btn>

      <v-btn border variant="text" @click="emit('detach-parallel')">
        Відокремити
      </v-btn>
    </div>

    <div class="d-flex flex-column flex-md-row ga-4">
      <div class="flex-grow-1">
        <b-input
          v-model="groupData.name"
          hide-details
          label="Назва групи"
          placeholder="Введіть текст">
        </b-input>
      </div>

      <div class="d-flex align-end ga-2">
        <queue-counter
          :length="queueLength"
          :value="queueNumber"
          @decrement="emit('decrement')"
          @increment="emit('increment')">
        </queue-counter>

        <v-btn
          v-tooltip="'Видалити групу'"
          density="comfortable"
          icon
          @click="emit('delete-group')">
          <v-icon>mdi-trash-can-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="d-flex flex-column flex-md-row align-md-end ga-4 mt-4">
      <div class="flex-grow-1">
        <b-autocomplete
          v-model="selectedUser"
          :items="availableUsers"
          :loading="$loading.isLoading('userList')"
          hide-details
          item-title="name"
          item-value="id"
          label="Користувачі"
          placeholder="Оберіть користувача"
          @focus="$directory.fill('userList')">
        </b-autocomplete>
      </div>

      <v-btn color="grey-darken-4" @click="handleAccessAddUser">
        <v-icon class="mr-2" icon="mdi-account-plus-outline"></v-icon>
        Додати користувача
      </v-btn>
    </div>

    <div>
      <div v-if="groupData.users?.length" class="mt-4">
        <div v-for="user in groupData.users" :key="user.id">
          <route-user-item :item="user">
            <template #actions>
              <v-btn
                border
                color="error"
                density="comfortable"
                icon
                variant="text"
                @click="removeRouteUser(user.id)">
                <v-icon icon="mdi-delete-outline"></v-icon>
              </v-btn>
            </template>
          </route-user-item>
          <v-divider></v-divider>
        </div>
      </div>
      <v-card v-else class="mt-3" border="dashed thin">
        <v-card-text>
          <span class="text-disabled d-flex justify-center">
            Користувачів не додано
          </span>
        </v-card-text>
      </v-card>
    </div>

    <v-divider class="my-4"></v-divider>

    <div class="d-flex flex-column flex-md-row ga-4">
      <div class="flex-1-1-0">
        <b-select
          v-model="groupData.agreementId"
          :items="
            $directory.get('templateRouteAgreements', groupData.agreement)
          "
          :loading="$loading.isLoading('templateRouteAgreements')"
          hide-details
          item-title="name"
          item-value="id"
          label="Ким погоджено"
          placeholder="Оберіть зі списку"
          @focus="$directory.fill('templateRouteAgreements')">
        </b-select>
      </div>

      <div class="flex-1-1-0">
        <b-select
          v-model="groupData.actionId"
          :items="$directory.get('templateRouteActions', groupData.action)"
          :loading="$loading.isLoading('templateRouteActions')"
          hide-details
          item-title="name"
          item-value="id"
          label="Тип"
          placeholder="Оберіть зі списку"
          @focus="$directory.fill('templateRouteActions')">
        </b-select>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Skeleton from '@/components/Skeleton.vue'
import QueueCounter from './QueueCounter.vue'
import RouteUserItem from './RouteUserItem.vue'
import { computed, reactive, ref, watch } from 'vue'
import { setSnackbar } from 'best-modules/plugins'
import { getDirectory } from '@/directory'
import {
  TemplateRoute,
  TemplateRouteSource,
  templateRouteUserCreate,
  templateRouteUserDelete,
} from '@/requests/templateRoute'

defineOptions({
  name: 'RouteGroupForm',
})

const props = defineProps<{
  group: TemplateRoute | null
  source: TemplateRouteSource
  queueNumber: number
  queueLength: number
  parallelGroupsCount: number
  usedUserIds: number[]
}>()

const emit = defineEmits<{
  'update:group': [group: TemplateRoute]
  'delete-group': []
  'add-to-stage': []
  'detach-parallel': []
  decrement: []
  increment: []
}>()

const selectedUser = ref<number | null>(null)

const groupData = reactive<TemplateRoute>({
  id: null,
  name: '',
  agreementId: null,
  actionId: null,
  queueNumber: null,
  users: [],
  agreement: null,
  action: null,
} as unknown as TemplateRoute)

const getGroupPayload = (group: TemplateRoute) => {
  return {
    id: group.id,
    name: group.name,
    agreementId: group.agreementId,
    actionId: group.actionId,
    templateId: group.templateId,
    documentAgreementId: group.documentAgreementId,
    queueNumber: group.queueNumber,
    users: Array.isArray(group.users) ? [...group.users] : [],
    agreement: group.agreement,
    action: group.action,
  } as TemplateRoute
}

const syncFromProps = () => {
  if (!props.group) return
  Object.assign(groupData, getGroupPayload(props.group))
}

syncFromProps()

watch(
  () => props.group,
  () => {
    syncFromProps()
  }
)

watch(
  groupData,
  value => {
    emit('update:group', getGroupPayload(value))
  },
  { deep: true }
)

const availableUsers = computed(() => {
  return getDirectory('userList').filter(user => {
    return !props.usedUserIds.includes(user.id)
  })
})

const buildUserCreatePayload = (userId: number) => {
  if (props.source === 'agreement') {
    return {
      userId,
      documentAgreementRouteId: groupData.id,
    }
  }

  return {
    userId,
    routeId: groupData.id,
  }
}

const addRouteUser = (userId: number) => {
  return templateRouteUserCreate(
    props.source,
    buildUserCreatePayload(userId)
  ).then(user => {
    groupData.users = [...(groupData.users || []), user]
    selectedUser.value = null
    return user
  })
}

const removeRouteUser = (id: number) => {
  return templateRouteUserDelete(props.source, id).then(res => {
    groupData.users = (groupData.users || []).filter(item => item.id !== id)
    setSnackbar({ text: 'Користувача видалено', color: 'success' })
    return res
  })
}

const handleAccessAddUser = () => {
  if (!selectedUser.value) {
    setSnackbar({ text: 'Спочатку оберіть користувача', color: 'error' })
    return
  }

  if (props.usedUserIds.includes(selectedUser.value)) {
    setSnackbar({ text: 'Користувача вже додано', color: 'error' })
    selectedUser.value = null
    return
  }

  return addRouteUser(selectedUser.value)
}
</script>

<style lang="scss" scoped>
.route-parallel-info {
  gap: 11px;
  padding: 11px 14px;
  border: 1px solid #d8c0f0;
  border-radius: 10px;
  background: #faf6fe;
  color: #7c3aed;
}

.route-parallel-info__title {
  color: #7c3aed;
  font-size: 13px;
  font-weight: 700;
}

.route-parallel-info__subtitle {
  margin-top: 1px;
  color: #9898a8;
  font-size: 12px;
}
</style>
